import api from './api';
import type { Notification } from '../types/notifications';

export interface PaginatedNotifications {
  notifications: Notification[];
  totalCount: number;
  hasMore: boolean;
  page: number;
  pageSize: number;
}

class NotificationService {
  /**
   * Gets all notifications for the current user with pagination.
   */
  async getAllNotifications(page = 1, pageSize = 20): Promise<PaginatedNotifications> {
    try {
      const response = await api.get(`/api/notifications?page=${page}&pageSize=${pageSize}`);
      if (response.data.success) {
        return response.data.data;
      }
      throw new Error(response.data.error || 'Failed to get notifications');
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'An unexpected error occurred');
    }
  }

  /**
   * Gets all unread notifications for the current user.
   */
  async getUnreadNotifications(): Promise<Notification[]> {
    try {
      const response = await api.get('/api/notifications/unread');
      if (response.data.success) {
        return response.data.data;
      }
      throw new Error(response.data.error || 'Failed to get unread notifications');
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'An unexpected error occurred');
    }
  }

  /**
   * Marks a notification as read.
   */
  async markAsRead(notificationId: string): Promise<Notification> {
    try {
      const response = await api.post(`/api/notifications/${notificationId}/read`);
      if (response.data.success) {
        return response.data.data;
      }
      throw new Error(response.data.error || 'Failed to mark notification as read');
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'An unexpected error occurred');
    }
  }

  /**
   * Marks all of the user's notifications as read.
   */
  async markAllAsRead(): Promise<{ message: string }> {
    try {
      const response = await api.post('/api/notifications/read-all');
      if (response.data.success) {
        return response.data.data;
      }
      throw new Error(response.data.error || 'Failed to mark all notifications as read');
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'An unexpected error occurred');
    }
  }
}

export default new NotificationService(); 